import React, { useCallback, useMemo } from 'react';
import type { EditorState } from 'lexical';
import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { LexicalDisplay } from '@components/Lexical/Display';
import { LexicalUtils } from '@components/Lexical/utils';
import { getCollectedReleasesPreview } from '@components/Notifications/shared';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ReplyPostContent } from '@components/modals/ReplyModal/Content';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  FeedPostCommentModalFragmentDoc,
  type FragmentType,
  getFragment,
  PostCommentDocument,
  useMutation,
} from '@graphql/reactQuery';
import { useLexicalValidate } from '@hooks/useLexicalValidate';
import { useLogError } from '@hooks/useLogErrorNew';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, SPACE } from '@utils/constants';
import { webappUriPath } from '@utils/url';

const MAX_RELEASES = 6;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FeedPostCommentModal on Post {
    id
    content
    lexicalContent
    webappUri
    __typename
    creator {
      id
      username
      avatar {
        id
        url
      }
      webappUri
      publicAddress
      displayName
      artist {
        id
        name
      }
    }
    ... on ActivityFeedGroupBackingPost {
      activityFeedGroup {
        information {
          __typename
          ... on ReleaseDroppedAggregate {
            release {
              id
              title
            }
          }
          ... on UserCollectedManySongsAggregate {
            numReleases
            collectionTypes
            collectedReleases {
              release {
                id
                title
                artist {
                  name
                }
              }
            }
          }
        }
      }
    }
    ... on ArtistDropPost {
      release {
        id
        title
      }
    }
  }
`);

export function FeedPostReplyModal({
  postFrag,
}: {
  postFrag: FragmentType<FeedPostCommentModalFragmentDoc>;
}) {
  const { closeCancelModal } = useModal();
  const { openToast } = useToast();
  const { mutateAsync, isLoading } = useMutation(PostCommentDocument, {});
  const logError = useLogError();

  const closeModal = useStableCallback(() => {
    closeCancelModal();
  });

  const post = getFragment(FeedPostCommentModalFragmentDoc, postFrag);

  const handleSubmitClicked = useCallback(
    async (lexicalState: EditorState) => {
      const textContent = LexicalUtils.generateTextForState(lexicalState);
      if (textContent.length) {
        try {
          const response = await mutateAsync({
            input: {
              postId: post.id,
              lexicalContent: lexicalState.toJSON(),
            },
          });
          if (response.data.addPostComment.__typename === 'MutationAddPostCommentSuccess') {
            openToast({
              text: 'Reply added',
              variant: 'success',
              ctaText: 'View',
              ctaHref: webappUriPath(post.webappUri),
            });
            closeModal();
          } else if (response.data.addPostComment.__typename === 'ValidationError') {
            openToast({ text: response.data.addPostComment.message, variant: 'error' });
          } else if (response.data.addPostComment.__typename === 'RateLimitError') {
            const message = 'Hit limit for replying posts comments';
            logError({
              error: new Error(message),
              action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
              errorType: ERROR_TYPE.RATE_LIMIT,
              isArtistOnlyPage: false,
              level: 'error',
              message,
              toast: message,
              pillar: 'POSTING',
              indexedTags: {
                postId: post.id,
              },
            });
          } else {
            throw new Error("Couldn't reply to post");
          }
        } catch (error) {
          logError({
            error,
            action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
            errorType: ERROR_TYPE.RATE_LIMIT,
            isArtistOnlyPage: false,
            level: 'error',
            message: "Couldn't reply to post",
            toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
            pillar: 'POSTING',
            indexedTags: {
              postId: post.id,
            },
          });
        }
      }
    },
    [mutateAsync, post.id, post.webappUri, openToast, closeModal, logError],
  );

  const lexicalContent = useLexicalValidate(post.lexicalContent);

  const postContent = useMemo(() => {
    switch (post.__typename) {
      case 'ActivityFeedGroupBackingPost':
        if (post.activityFeedGroup.information.__typename === 'ReleaseDroppedAggregate') {
          return (
            <p className="text-base600">
              Dropped{SPACE}
              <span className="font-semibold">
                {post.activityFeedGroup.information.release.title}
              </span>
            </p>
          );
        } else if (
          post.activityFeedGroup.information.__typename === 'UserCollectedManySongsAggregate'
        ) {
          const { collectedReleases } = post.activityFeedGroup.information;

          const lines = getCollectedReleasesPreview({
            collectedReleases,
            maxReleases: MAX_RELEASES,
          });

          return (
            <p className="test-base600">
              Collected{SPACE}
              {lines}
            </p>
          );
        } else {
          return post.content;
        }

      default:
        return post.content;
    }
  }, [post]);

  return (
    <StandardModal
      title="Reply"
      closeModal={closeModal}
      bodyWithoutPaddingBottom
      mobileFullScreen
      bodyClassName="md:overflow-auto"
      body={
        <ReplyPostContent
          isSubmitting={isLoading}
          postContent={
            lexicalContent ? (
              <LexicalDisplay showMentionsHoverCard={false} state={lexicalContent} />
            ) : (
              postContent
            )
          }
          postCreator={post.creator}
          isLoading={false}
          onSubmit={handleSubmitClicked}
          autoFocus
        />
      }
    />
  );
}
